const _excluded = ["children", "className", "variants", "initial", "animate", "exit", "autoplay", "autoplayLoop", "autoplayInterval", "autoplayOffset", "stopAutoplayOnInteraction", "transition", "drag", "dragConstraints", "dragElastic", "swipeConfidenceThreshold", "hideButtonsOnEdges", "carouselButtons", "carouselIndicators", "isFixedHeight"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import { useRef, useState, useCallback, Children, isValidElement, cloneElement, useEffect } from 'react';
import { LazyMotion, domAnimation, AnimatePresence, m } from 'framer-motion';
import styled from 'styled-components';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion.js';
import { ScreenReaderOnly } from '../ScreenReaderOnly.js';
import { CarouselButtons } from './CarouselButtons.js';
import { CarouselContext } from './CarouselContext.js';
import { CarouselIndicators } from './CarouselIndicators.js';
import { jsx, jsxs } from 'react/jsx-runtime';

/* eslint-disable @cbhq/react-no-untranslated-string -- TODO Fix this */
const CAROUSEL_DEFAULT_TRANSITION = {
  x: {
    type: 'spring',
    stiffness: 300,
    damping: 30
  },
  opacity: {
    duration: 0.2
  }
};
const CAROUSEL_DEFAULT_VARIANTS = {
  enter: offset => ({
    x: offset > 0 ? 1000 : -1000,
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: offset => ({
    zIndex: 0,
    x: offset < 0 ? 1000 : -1000,
    opacity: 0,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO Fix this
    position: 'absolute'
  })
};
const CAROUSEL_REDUCED_MOTION_VARIANTS = {
  center: {
    zIndex: 1
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO Fix this
  exit: {
    zIndex: 0,
    position: 'absolute'
  }
};
const wrap = (min, max, v) => {
  const rangeSize = max - min;
  return ((v - min) % rangeSize + rangeSize) % rangeSize + min;
};
function Carousel(_ref) {
  let children = _ref.children,
    className = _ref.className,
    _ref$variants = _ref.variants,
    variants = _ref$variants === void 0 ? CAROUSEL_DEFAULT_VARIANTS : _ref$variants,
    _ref$initial = _ref.initial,
    initial = _ref$initial === void 0 ? 'enter' : _ref$initial,
    _ref$animate = _ref.animate,
    animate = _ref$animate === void 0 ? 'center' : _ref$animate,
    _ref$exit = _ref.exit,
    exit = _ref$exit === void 0 ? 'exit' : _ref$exit,
    autoplay = _ref.autoplay,
    autoplayLoop = _ref.autoplayLoop,
    _ref$autoplayInterval = _ref.autoplayInterval,
    autoplayInterval = _ref$autoplayInterval === void 0 ? 3000 : _ref$autoplayInterval,
    _ref$autoplayOffset = _ref.autoplayOffset,
    autoplayOffset = _ref$autoplayOffset === void 0 ? 1 : _ref$autoplayOffset,
    stopAutoplayOnInteraction = _ref.stopAutoplayOnInteraction,
    _ref$transition = _ref.transition,
    transition = _ref$transition === void 0 ? CAROUSEL_DEFAULT_TRANSITION : _ref$transition,
    _ref$drag = _ref.drag,
    drag = _ref$drag === void 0 ? 'x' : _ref$drag,
    _ref$dragConstraints = _ref.dragConstraints,
    dragConstraints = _ref$dragConstraints === void 0 ? {
      left: 0,
      right: 0
    } : _ref$dragConstraints,
    _ref$dragElastic = _ref.dragElastic,
    dragElastic = _ref$dragElastic === void 0 ? 1 : _ref$dragElastic,
    _ref$swipeConfidenceT = _ref.swipeConfidenceThreshold,
    swipeConfidenceThreshold = _ref$swipeConfidenceT === void 0 ? 8000 : _ref$swipeConfidenceT,
    hideButtonsOnEdges = _ref.hideButtonsOnEdges,
    _ref$carouselButtons = _ref.carouselButtons,
    carouselButtons = _ref$carouselButtons === void 0 ? /*#__PURE__*/jsx(CarouselButtons, {}) : _ref$carouselButtons,
    _ref$carouselIndicato = _ref.carouselIndicators,
    carouselIndicators = _ref$carouselIndicato === void 0 ? /*#__PURE__*/jsx(CarouselIndicators, {}) : _ref$carouselIndicato,
    _ref$isFixedHeight = _ref.isFixedHeight,
    isFixedHeight = _ref$isFixedHeight === void 0 ? false : _ref$isFixedHeight,
    props = _objectWithoutProperties(_ref, _excluded);
  const hasInteracted = useRef(false);
  const autoplayTimeoutId = useRef();
  const _useState = useState([0, 0]),
    _useState2 = _slicedToArray(_useState, 2),
    _useState2$ = _slicedToArray(_useState2[0], 2),
    activeSlideOffset = _useState2$[0],
    offset = _useState2$[1],
    setSlide = _useState2[1];
  const noAnimation = usePrefersReducedMotion();
  if (noAnimation) {
    autoplay = false;
    if (variants === CAROUSEL_DEFAULT_VARIANTS) {
      variants = CAROUSEL_REDUCED_MOTION_VARIANTS;
    }
  }
  const paginate = useCallback((offset, ignoreInteraction) => {
    clearTimeout(autoplayTimeoutId.current);
    if (!ignoreInteraction) hasInteracted.current = true;
    setSlide(([slideOffset]) => [slideOffset + offset, offset]);
  }, []);
  const slides = Children.toArray(children).map((child, slideIndex) => {
    if (! /*#__PURE__*/isValidElement(child)) return child;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO Fix this
    return /*#__PURE__*/cloneElement(child, {
      slideIndex
    });
  });

  // We only have N slides, but we paginate them absolutely (ie 1, 2, 3, 4, 5...) and
  // then wrap that within 0-N to find our slide in the array below. By passing an
  // absolute activeSlideIndex as the `motion` component's `key` prop, `AnimatePresence` will
  // detect it as an entirely new element. So you can infinitely paginate as few as 1 elements.
  const activeSlideIndex = wrap(0, slides.length, activeSlideOffset);
  useEffect(() => {
    if (!autoplay || !autoplayLoop && activeSlideIndex === slides.length - 1 || stopAutoplayOnInteraction && hasInteracted.current) return;
    autoplayTimeoutId.current = window.setTimeout(() => paginate(autoplayOffset, true), autoplayInterval);
    return () => clearTimeout(autoplayTimeoutId.current);
  }, [activeSlideIndex, slides.length, paginate, noAnimation, autoplay, autoplayLoop, autoplayInterval, autoplayOffset, stopAutoplayOnInteraction]);
  return /*#__PURE__*/jsx(Container, _objectSpread(_objectSpread({
    className: className,
    $hasDefaultHeight: !isFixedHeight
  }, props), {}, {
    children: /*#__PURE__*/jsx(Wrapper, {
      children: /*#__PURE__*/jsxs(CarouselContext.Provider, {
        value: {
          activeSlideIndex,
          numberOfSlides: slides.length,
          hideButtonsOnEdges,
          paginate
        },
        children: [/*#__PURE__*/jsx(LazyMotion, {
          features: domAnimation,
          children: /*#__PURE__*/jsxs(AnimatePresence, {
            initial: false,
            custom: offset,
            children: [/*#__PURE__*/jsx(m.div, {
              style: {
                position: isFixedHeight ? 'absolute' : 'inherit',
                width: '100%',
                height: '100%'
              },
              custom: offset,
              variants: variants,
              initial: initial,
              animate: animate,
              exit: exit,
              transition: transition,
              drag: drag,
              dragConstraints: dragConstraints,
              dragElastic: dragElastic
              // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- TODO Fix this
              ,

              onDragEnd: (event, {
                offset,
                velocity
              }) => {
                const swipePower = Math.abs(offset.x) * velocity.x;
                if (swipePower < -swipeConfidenceThreshold) paginate(1);else if (swipePower > swipeConfidenceThreshold) paginate(-1);
              },
              children: slides[activeSlideIndex]
            }, activeSlideOffset), /*#__PURE__*/jsxs(ScreenReaderOnly, {
              "aria-live": "polite",
              "aria-atomic": "true",
              children: ["Showing slide ", activeSlideIndex + 1, " of ", slides.length]
            })]
          })
        }), carouselButtons, carouselIndicators]
      })
    })
  }));
}
const Container = styled.div`
  width: 100%;
  height: ${({
  $hasDefaultHeight
}) => $hasDefaultHeight ? '700px' : 'inherit'};

  overflow-x: ${({
  $hasDefaultHeight: $hasDefaultHeight
}) => $hasDefaultHeight ? 'hidden' : 'visible'};

  user-select: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;
const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;
export { Carousel, wrap };
//# sourceMappingURL=Carousel.js.map
